import { action, observable, reaction, runInAction } from 'mobx'
import dateFormat from 'dateformat'
import { StoreExt } from '@utils/reactExt'
import {
  BETTING_HISTORY_TAB_KEY,
  BODY_SCROLL_HIDDEN,
  BODY_X_SCROLLABLE,
  BUBBLE_SECOND_DURATION,
  COMPONENT_SECOND_DURATION,
  COOKIE_KEYS,
  LOCALSTORAGE_KEYS,
  MATCHUP_DETAIL_STATIC_FILTER_INDEX,
  SERVER_STATE,
  SIDE_MENU,
  TOUCH_ACTION_DISABLED,
  TOUCH_ACTION_ENABLED,
} from '@constants/index'
import uuid from 'uuid'
import { message } from 'antd'
import _ from 'lodash'
import React from 'react'
import { isIOS, isMobile } from 'react-device-detect'
import cronParser from 'cron-parser'
import dayjs from 'dayjs'

import { GlobalSettings } from '@protocol/game'
import {
  chattingStore,
  cheerForTeamStore,
  clubStore,
  componentStore,
  matchupStore,
  messageStore,
  oddsStore,
  portalSocketStore,
  routerStore,
  userStore,
} from '@store/index'
import { FEED_SUPPLIER, GlobalStoreKey } from '@v2/constants/common'
import { safeJsonParse } from '@v2/utils/global-utils'
import { SimpleMatchTrack } from '@store/matchupStore'
import { ArrDayText, ENABLE_MIN_BET_RATE } from '@game/util'
import { GeneralPopupOptions, GeneralPopupProps } from '@views/Popup/General'
import { DialogOptions, DialogProps } from '@views/Popup/Dialog'
import {
  AccountTransferOptions,
  BetLimitScoreInfo,
  ChampPokerDeepLink,
  ExtraOddsItemAvailableFoldCondition,
  MatchupEtcFilterAlertOptions,
} from 'adv-lib/src/v2/interface/common'
import StorageUtils from '@utils/storage-utils'
import * as Utils from '@utils/utils'
import { TOAST_TYPE } from '@utils/utils'

import { UserActivityInfo } from '@interface/user-activity'
import { ChatContentButtonOption } from '@interface/chat'
import { MajorUserGroupThemeInfo, UsageGuideVideoPopupOptions } from '@interface/global-value'
import PopupUtils from '@utils/popup-utils'

export type SideBarTheme = 'dark' | 'light'

export enum DialogType {
  SINGLE,
  CHOICE,
}

export enum BubbleTextType {
  NONE,
  SUCCESS,
  LIVE_SUCCESS,
  DEFAULT_ALERT,
  MAX_BET_ALERT,
  MESSAGE_ALERT
}

export enum BubbleType {
  BET_SLIP,
  COMMON_BET_SLIP,
  MESSAGE_ALERT
}


export interface BubbleData {
  type: BubbleTextType
  visible: boolean
  text: string
  timerId: any
}

const android = window.android

export class GlobalStore extends StoreExt {
  /**
   * 사이드메뉴 접기
   *
   * @type {boolean}
   * @memberof GlobalStore
   */
  @observable
  visibleSideBar = false


  /**
   * 테마
   *
   * @type {SideBarTheme}
   * @memberof GlobalStore
   */
  @observable
  sideBarTheme: SideBarTheme = (localStorage.getItem(LOCALSTORAGE_KEYS.SIDE_BAR_THEME) as SideBarTheme) || 'light'

  /**
   * 사이드 메뉴 오픈키
   *
   * @type {string[]}
   * @memberof GlobalStore
   */
  @observable
  navOpenKeys: string[] = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEYS.NAV_OPEN_KEYS)) || []

  @observable
  visibleEvent = false

  @observable
  visibleAttendance = false

  @observable
  visibleGiftbox = false

  @observable
  visibleChargingComplete = false

  @observable
  visibleVocPopup = false

  /**
   * 게임 처음 접속시 나오는 파트너 배너는 기본값이 true.
   */
  @observable
  visiblePartnershipBannerPopup = false

  @observable
  visibleForefrontPopup = false

  forefrontPopupOnClose: () => void = null

  @observable
  visibleGameSetting = false

  @observable
  visibleScashCharge = false

  @observable
  visibleInitialPopup = false

  @observable
  visibleNoticeDetail = false

  @observable
  visibleMissionCompletePopup = false

  @observable
  visibleGuideNoticePopup = false

  @observable
  visibleClubNoticePopup = false

  @observable
  visibleFirstBetPopup = false

  @observable
  visibleAchieveGoalPopup = false

  @observable
  visibleGuideCompletePopup = false

  @observable
  visibleBetCancelFailPopup = false

  @observable
  visibleOneFoldPopup = false

  @observable
  visibleMatchupDetailOneFoldPopup = false

  @observable
  visibleBettingListPopup = false

  @observable
  visibleProfileImgViewPopup = false

  @observable
  visibleQuickMessagePopup = false

  @observable
  showBettingListItemSelectBtn = false

  @observable
  showBettingListFoldItemSelectBtn = false

  @observable
  visibleComponentBetSlip = false

  @observable
  visibleBetSuccess = false

  betSuccessTodayInvisibleCookieKey = COOKIE_KEYS.BETTING_SUCCESS_POPUP_INVISIBLE_FOR_MATCHUP

  @observable
  visibleBetCoupon = false

  @observable
  visibleSameMatchPopup = false

  @observable
  visibleBubbleBetList = false

  @observable
  visibleLiveBetCounter = false

  @observable
  somethingPopup = false

  @observable
  chattingLastMessage = ''

  @observable
  generalPopupQueue: GeneralPopupProps[] = []

  @observable
  dialogQueue: DialogProps[] = []

  // 뒤로가기로 닫을 팝업 ID
  @observable
  backButtonClosePopupId: string = null

  @observable
  userActivityInfo: UserActivityInfo

  @observable
  activityScoreNoticeQueue: UserActivityInfo[] = []

  @observable
  currentDialog: DialogProps

  @observable
  currentGeneralPopup: GeneralPopupProps

  @observable
  globalSettings: GlobalSettings[]

  @observable
  splash = false

  @observable
  serverState = SERVER_STATE.ON

  @observable
  visibleNoAccess = false

  @observable
  clubMessageMode = false

  @observable
  visibleMissionListPopup = false

  @observable
  visibleCouponInputPopup = false

  @observable
  visibleTutorialPopup = false

  @observable
  visibleTutorialVideoPopup = false

  @observable
  usageGuideVideoPopupPlaybackLocked = false

  @observable
  visibleBaccaratGuidePopup = false

  @observable
  visibleCasinoOpenPopup = false

  @observable
  visibleGuideBonusReceiveButton = false

  @observable
  visibleChangeProfile = false

  @observable
  visibleTodayPick = false // 프리미엄 분석가 상세페이지

  @observable
  visibleTodayPickList = false // 프리미엄 분석가 목록페이지

  @observable
  premiumUserId = null

  @observable
  visibleUserPick = false

  @observable
  visibleAdvNews = false

  @observable
  visibleGuide = false

  @observable
  visibleGuideBonusDelay = true

  @observable
  visibleSelectLiveMatchType = false

  @observable
  visibleSelectCertification = false

  @observable
  visibleBetCompleteAni = false

  @observable
  visibleComponentBetCompleteAni = false

  @observable
  visibleMessageAlertAni = false

  @observable
  visibleUrgencyMessagePopup = false

  @observable
  visibleMatchFilterPopup = false

  @observable
  visibleMatchupFilterSelectPopup = false

  // 베팅내역 상단 배너 노출여부
  @observable
  visibleBettingHistoryBanner = false

  visibleBetCompleteAniTimer = null

  menuAvailableList = []

  @observable
  isAndroidAppKeyboardVisible = false

  // @observable
  // bubbleData: BubbleData = { type: BubbleTextType.NONE, visible: false, text: '', timerId: null }

  @observable
  bubbleData: { [id: number]: BubbleData } = {
    [BubbleType.BET_SLIP]: { type: BubbleTextType.NONE, visible: false, text: '', timerId: null },
    [BubbleType.COMMON_BET_SLIP]: { type: BubbleTextType.NONE, visible: false, text: '', timerId: null },
    [BubbleType.MESSAGE_ALERT]: { type: BubbleTextType.NONE, visible: false, text: '', timerId: null },
  }

  @observable
  scrollTop = 0
  filterScrollTop = 0

  bettingHistoryTabInitialTabKey = BETTING_HISTORY_TAB_KEY.ONGOING

  matchupDetailTabInitialIndex = MATCHUP_DETAIL_STATIC_FILTER_INDEX.TOTAL

  liveBetCounterVisibleTimer = null

  @observable
  visibleSendMessagePopup = false

  authenticationPopup = null

  ignoreVisibleChangeHandler = false

  @observable
  visibleMatchupDetailPopup = false

  @observable
  visibleTodayUserPickBanner = true

  @observable
  visibleModalList: { key: string }[] = [] // <Modal/> 컴포넌트 개수가 1개 이상이면 <body> 에 body-scroll-hidden 을 붙여 준다.

  @observable
  visibleReplyPickComment = false

  @observable
  visibleEditPickComment = false

  @observable
  visibleUserTitle = true

  @observable
  visibleAccountTransferPopup = false

  @observable
  accountTransferPopupCallback: () => Promise<void>

  @observable
  visibleMatchupBottomBannerPopup = false

  @observable
  visiblePopularSearchTerm = true

  visibilityChangeTimer = null

  constructor() {
    super()
    this.setupVisibleModalCountReaction()
    this.setupAndroidAppVisualViewportKeyboardWatcher()
    document.addEventListener('visibilitychange', this.documentVisibleChangeHandler, true)
    document.addEventListener('click', this.documentClickHandler)
  }

  private setupAndroidAppVisualViewportKeyboardWatcher(): void {
    try {
      if (isMobile && _.isNil((window as any).android)) return
      const vv = window.visualViewport
      if (!vv) return

      const THRESHOLD = 120 // 이 값(px) 이상 viewport가 줄어들면 키보드 오픈으로 판단
      let baseline = vv.height
      let prevOpen = false

      const update = () => {
        const delta = Math.max(0, baseline - vv.height)
        const open = delta > THRESHOLD

        if (open !== prevOpen) {
          prevOpen = open
          this.setIsAndroidAppKeyboardVisible(open)
        }

        // 닫힌 상태일 때만 기준선 업데이트
        if (!open) {
          baseline = vv.height
        }
      }

      vv.addEventListener('resize', update)
      vv.addEventListener('scroll', update)
      update()
    } catch (err) {
      console.error(err)
    }
  }


  @action
  setIsAndroidAppKeyboardVisible(isAndroidAppKeyboardVisible: boolean) {
    this.isAndroidAppKeyboardVisible = isAndroidAppKeyboardVisible
  }

  setupVisibleModalCountReaction() {
    // <Modal> 개수가 1개 이상이면 body-scroll-hidden 추가,
    // <Modal> 개수가 0개 이하면 body-scroll-hidden 제거.
    // 아래 기능은 자동으로 동작한다.
    reaction(
      () => this.visibleModalList,
      count => {
        console.info('======== visible modal count', _.size(count), isMobile)
        if (_.size(count) > 0) {
          this.addBodyScrollHiddenClass()
        } else {
          this.removeBodyScrollHiddenClass()
        }
      },
    )
  }

  setIgnoreVisibleChangeHandler(ignore: boolean) {
    this.ignoreVisibleChangeHandler = ignore
  }

  documentVisibleChangeHandler = () => {
    if (this.visibilityChangeTimer) {
      return
    }

    this.visibilityChangeTimer = setTimeout(() => {
      try {
        const isHidden = document.hidden

        if (isHidden && this.ignoreVisibleChangeHandler) {
          return
        }

        if (!isHidden && this.ignoreVisibleChangeHandler) {
          this.setIgnoreVisibleChangeHandler(true)
          return
        }

        // 두번 실행 되는 현상을 방지 하기 위해.
        // visibilitychange 이벤트는 visibilityState 가 hidden 과 visible 두가지 상태 모두 실행 된다.
        if (document.visibilityState === 'visible') {
          this.getGlobalSettings().catch(console.error)
        }

        oddsStore.documentVisibleChangeHandler(isHidden)
        portalSocketStore.documentVisibleChangeHandler(isHidden)
        chattingStore.documentVisibleChangeHandler(isHidden).catch(console.error)
        userStore.documentVisibleChangeHandler(isHidden)
        clubStore.documentVisibleChangeHandler(isHidden)
        messageStore.documentVisibleChangeHandler(isHidden)
        componentStore.documentVisibleChangeHandler(isHidden)
        matchupStore.documentVisibleChangeHandler(isHidden)
        cheerForTeamStore.documentVisibleChangeHandler(isHidden)
      } catch (err) {
        Utils.showToast(TOAST_TYPE.ERROR, JSON.stringify(err))
        console.error(err)
      } finally {
        this.visibilityChangeTimer = null
      }
    }, 50)
  }

  /**
   * 안드로이드 환경에서 다른 UI 동작할 때 가상 키보드를 호출했던 HTMLElement Blur 처리.
   * 안드로이드에서 가상 키보드를 닫혔을 시 HTMLElement의 focus가 해제되지 않은 현상 존재.
   * 아이폰의 경우 가상 키보드 활성 여부와 input의 focus 여부가 일치.
   */
  focusedInputElementBlurByAndroid() {
    if (!isMobile) {
      return
    }

    const userAgent = _.toLower(navigator.userAgent)
    if (!_.includes(userAgent, 'android')) {
      return
    }

    const element = document?.activeElement
    if (!element) {
      return
    }

    if (_.includes(element?.className, 'input')) {
      (element as HTMLElement).blur()
    }
  }

  documentClickHandler = () => {
    this.focusedInputElementBlurByAndroid()
  }

  @action
  cleanup = () => {
    this.visibleSideBar = false
    this.visibleEvent = false
    this.visibleAttendance = false
    this.visibleGiftbox = false
    this.visibleChargingComplete = false
    this.visibleVocPopup = false
    this.visiblePartnershipBannerPopup = false
    this.visibleForefrontPopup = false
    this.forefrontPopupOnClose = null
    this.visibleGameSetting = false
    this.visibleScashCharge = false
    this.visibleInitialPopup = false
    this.visibleNoticeDetail = false
    this.visibleMissionCompletePopup = false
    this.visibleGuideNoticePopup = false
    this.visibleClubNoticePopup = false
    this.visibleFirstBetPopup = false
    this.visibleAchieveGoalPopup = false
    this.visibleGuideCompletePopup = false
    this.visibleBetCancelFailPopup = false
    this.visibleOneFoldPopup = false
    this.visibleMatchupDetailOneFoldPopup = false
    this.visibleBettingListPopup = false
    this.visibleProfileImgViewPopup = false
    this.visibleQuickMessagePopup = false
    this.visibleComponentBetSlip = false
    this.visibleBetSuccess = false
    this.visibleBetCoupon = false
    this.visibleSameMatchPopup = false
    this.visibleBubbleBetList = false
    this.visibleMissionListPopup = false
    this.visibleCouponInputPopup = false
    this.visibleTutorialPopup = false
    this.visibleTutorialVideoPopup = false
    this.usageGuideVideoPopupPlaybackLocked = false
    this.visibleBaccaratGuidePopup = false
    this.visibleCasinoOpenPopup = false
    this.dialogQueue = []
    this.generalPopupQueue = []
    this.activityScoreNoticeQueue = []
    this.currentDialog = undefined
    this.currentGeneralPopup = undefined
    this.splash = false
    this.visibleGuideBonusReceiveButton = false
    this.visibleChangeProfile = false
    this.visibleTodayPick = false
    this.visibleTodayPickList = false
    this.visibleUserPick = false
    this.visibleAdvNews = false
    this.visibleGuide = false
    this.visibleGuideBonusDelay = true
    this.visibleSelectLiveMatchType = false
    this.visibleSelectCertification = false
    this.visibleLiveBetCounter = false
    this.visibleSendMessagePopup = false
    this.visibleBetCompleteAni = false
    this.visibleComponentBetCompleteAni = false
    this.visibleMessageAlertAni = false
    this.bettingHistoryTabInitialTabKey = BETTING_HISTORY_TAB_KEY.ONGOING
    this.matchupDetailTabInitialIndex = MATCHUP_DETAIL_STATIC_FILTER_INDEX.TOTAL
    this.liveBetCounterVisibleTimer = null
    this.visibleNoAccess = false
    this.visibleMatchupDetailPopup = false
    this.visibleTodayUserPickBanner = true
    this.visibleUrgencyMessagePopup = false
    this.visibleMatchFilterPopup = false
    this.visibleMatchupFilterSelectPopup = false
    this.visibleModalList = []
    this.visibleReplyPickComment = false
    this.visibleEditPickComment = false
    this.visibleUserTitle = true
    this.visibleAccountTransferPopup = false
    this.visibleMatchupBottomBannerPopup = false
    this.closeAuthenticationPopup()
    this.initBubble()
  }

  initBubble = () => {
    this.setBubblePopupType(BubbleTextType.NONE, BubbleType.BET_SLIP)
    this.setBubblePopupVisible(false, BubbleType.BET_SLIP)
    this.setBubblePopupType(BubbleTextType.NONE, BubbleType.COMMON_BET_SLIP)
    this.setBubblePopupVisible(false, BubbleType.COMMON_BET_SLIP)
    this.setBubblePopupVisible(false, BubbleType.MESSAGE_ALERT)
  }

  @action
  syncGlobal(key: string, value: string) {
    if (_.isNil(value) || value === '') {
      _.remove(this.globalSettings, { key })
    } else {
      const result = _.find(this.globalSettings, { key })
      if (result) {
        result.value = value
      } else {
        this.globalSettings.push({ key, value })
      }

      if (key === GlobalStoreKey.SPORTS_FEED_MARKET_PROVIDER) {
        if (!_.isNil(matchupStore.getMarketProvider())
          && _.isEqual(matchupStore.marketProviderData, JSON.parse(value))) {
          window.location.href = '/'
        }
      }
    }
  }

  @action
  setScrollTop = (scrollTop: number) => {
    this.scrollTop = scrollTop
  }

  // 필터 팝업에서 추가BET을 열고 닫을 경우 scrollTop이 0이 되어 별도로 생성하였습니다.
  @action
  setFilterScrollTop = (filterScrollTop: number) => {
    this.filterScrollTop = filterScrollTop
  }

  @action
  toggleSplash = (visible: boolean) => {
    this.splash = visible
  }

  @action
  isSplash = () => {
    return this.splash
  }

  @action
  setVisibleSideBar = (visible: boolean) => {
    this.visibleSideBar = visible
    this.updateVisibleModalList(visible, 'visibleSideBar')
  }

  @action
  changeSiderTheme = (theme: SideBarTheme) => {
    this.sideBarTheme = theme
    StorageUtils.saveSideBarTheme(theme)
  }

  @action
  setOpenKeys = (openKeys: string[]) => {
    this.navOpenKeys = openKeys
    StorageUtils.saveNavOpenKeys(openKeys)
  }

  @action
  setVisibleEvent = (visible: boolean): void => {
    this.visibleEvent = visible
    this.updateVisibleModalList(visible, 'visibleEvent')
  }

  @action
  setVisibleAttendance = (visible: boolean): void => {
    this.visibleAttendance = visible
    this.updateVisibleModalList(visible, 'visibleAttendance')
  }

  @action
  setVisibleGiftBox = (visible: boolean) => {
    this.visibleGiftbox = visible
    this.updateVisibleModalList(visible, 'visibleGiftbox')
  }

  @action
  setVisibleChargingComplete = (visible: boolean) => {
    this.visibleChargingComplete = visible
    this.updateVisibleModalList(visible, 'visibleChargingComplete')
  }

  @action
  setVisibleVocPopup = (visible: boolean) => {
    if (this.visibleMatchFilterPopup) {
      this.visibleVocPopup = false
      this.updateVisibleModalList(false, 'visibleVocPopup')
    } else {
      this.visibleVocPopup = visible
      this.updateVisibleModalList(visible, 'visibleVocPopup')
    }
  }

  @action
  setVisiblePartnershipBannerPopup = (visible: boolean) => {
    this.visiblePartnershipBannerPopup = visible
    this.updateVisibleModalList(visible, 'visiblePartnershipBannerPopup')
  }

  @action
  setVisibleForefrontPopup = (visible: boolean) => {
    if (this.visibleMatchFilterPopup) {
      this.visibleForefrontPopup = false
      this.updateVisibleModalList(false, 'visibleForefrontPopup')
    } else {
      this.visibleForefrontPopup = visible
      this.updateVisibleModalList(visible, 'visibleForefrontPopup')
    }
  }

  // visibleMatchFilterPopup과 충돌해서 실제로는 안 열린 경우 false를 반환한다.
  @action
  openForefrontPopup = (onClose?: () => void): boolean => {
    this.forefrontPopupOnClose = onClose
    this.setVisibleForefrontPopup(true)
    if (!this.visibleForefrontPopup) {
      this.forefrontPopupOnClose = null
      return false
    }
    return true
  }

  @action
  closeForefrontPopup = () => {
    const onClose = this.forefrontPopupOnClose
    this.forefrontPopupOnClose = null
    this.setVisibleForefrontPopup(false)
    onClose?.()
  }

  @action
  setClubMessageMode = (clubMessageMode: boolean) => {
    this.clubMessageMode = clubMessageMode
  }

  @action
  setVisibleGameSetting = (visible: boolean) => {
    this.visibleGameSetting = visible
    this.updateVisibleModalList(visible, 'visibleGameSetting')
  }

  @action
  setVisibleScashCharge = (visible: boolean) => {
    this.visibleScashCharge = visible
    this.updateVisibleModalList(visible, 'visibleScashCharge')
  }

  @action
  setVisibleInitialPopup = (visible: boolean) => {
    this.visibleInitialPopup = visible
    this.updateVisibleModalList(visible, 'visibleInitialPopup')
  }

  @action
  setVisibleMissionCompletePopup = (visible: boolean) => {
    this.visibleMissionCompletePopup = visible
    this.updateVisibleModalList(visible, 'visibleMissionCompletePopup')
  }

  @action
  setVisibleGuideNoticePopup = (visible: boolean) => {
    this.visibleGuideNoticePopup = visible
    this.updateVisibleModalList(visible, 'visibleGuideNoticePopup')
  }

  @action
  setVisibleClubNoticePopup = (visible: boolean) => {
    this.visibleClubNoticePopup = visible
    this.updateVisibleModalList(visible, 'visibleClubNoticePopup')
  }

  @action
  setVisibleFirstBetPopup = (visible: boolean) => {
    this.visibleFirstBetPopup = visible
    this.updateVisibleModalList(visible, 'visibleFirstBetPopup')
  }

  @action
  setVisibleAchieveGoalPopup = (visible: boolean) => {
    this.visibleAchieveGoalPopup = visible
    this.updateVisibleModalList(visible, 'visibleAchieveGoalPopup')
  }

  @action
  setVisibleGuideCompletePopup = (visible: boolean) => {
    this.visibleGuideCompletePopup = visible
    this.updateVisibleModalList(visible, 'visibleGuideCompletePopup')
  }

  @action
  setVisibleBetCancelFailPopup = (visible: boolean) => {
    this.visibleBetCancelFailPopup = visible
    this.updateVisibleModalList(visible, 'visibleBetCancelFailPopup')
  }

  @action
  setVisibleOneFoldPopup = (visible: boolean) => {
    this.visibleOneFoldPopup = visible
    this.updateVisibleModalList(visible, 'visibleOneFoldPopup')
  }

  @action
  setVisibleMatchupDetailOneFoldPopup = (visible: boolean) => {
    this.visibleMatchupDetailOneFoldPopup = visible
    this.updateVisibleModalList(visible, 'visibleMatchupDetailOneFoldPopup')
  }

  @action
  setVisibleBettingListPopup
    = (visible: boolean, showSelectBtn = false, showFoldSelectBtn = false, onClick?: () => void) => {
    // PC 버전의 채팅창 또는 메시지창에서 배팅내역을 띄웠다면... 선택버튼 출력을 위해...
    if (visible) {
      PopupUtils.showBettingListSelectorPopup()
      this.showBettingListItemSelectBtn = showSelectBtn
      this.showBettingListFoldItemSelectBtn = showFoldSelectBtn
    } else {
      this.showBettingListItemSelectBtn = false
      this.showBettingListFoldItemSelectBtn = false
      if (onClick) {
        onClick()
      }
    }

    this.updateVisibleModalList(visible, 'visibleBettingListPopup')
  }

  @action
  setVisibleProfileImgViewPopup = (visible: boolean) => {
    this.visibleProfileImgViewPopup = visible
    this.updateVisibleModalList(visible, 'visibleProfileImgViewPopup')
  }

  @action
  setVisibleQuickMessagePopup = (visible: boolean) => {
    this.visibleQuickMessagePopup = visible
    this.updateVisibleModalList(visible, 'visibleQuickMessagePopup')
  }

  @action
  setVisibleComponentBetSlip = (visible: boolean) => {
    this.visibleComponentBetSlip = visible
    this.updateVisibleModalList(visible, 'visibleComponentBetSlip')
  }

  setBetSuccessTodayInvisibleCookieKey = (key: COOKIE_KEYS) => {
    this.betSuccessTodayInvisibleCookieKey = key
  }

  @action
  setVisibleBetSuccessPopup = (visible: boolean, betSuccessTodayInvisibleCookieKey?: COOKIE_KEYS) => {
    if (betSuccessTodayInvisibleCookieKey) {
      this.setBetSuccessTodayInvisibleCookieKey(betSuccessTodayInvisibleCookieKey)
    }

    this.visibleBetSuccess = visible
    this.updateVisibleModalList(visible, 'visibleBetSuccess')
  }

  @action
  setVisibleBetCoupon = (visible: boolean) => {
    this.visibleBetCoupon = visible
    this.updateVisibleModalList(visible, 'visibleBetCoupon')
  }

  @action
  setVisibleSameMatchPopup = (visible: boolean) => {
    this.visibleSameMatchPopup = visible
    this.updateVisibleModalList(visible, 'visibleSameMatchPopup')
  }

  @action
  setVisibleBubbleBetList = (visible: boolean) => {
    this.visibleBubbleBetList = visible
    this.updateVisibleModalList(visible, 'visibleBubbleBetList')
  }

  @action
  addGeneralPopupProperties = (
    contentBody: React.ReactElement,
    title: string,
    options: GeneralPopupOptions = {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false, /* full modal 하단 플로팅 닫기 버튼*/
      closeCallback: null,
      contentClassName: '',
    },
    titleCustom: React.ReactNode = null,
    popupId: string = null,
  ): void => {
    if (this.currentGeneralPopup && this.currentGeneralPopup.popupId === popupId) {
      return
    }

    const defaultOptions = {
      wrapClassName: '',
      className: '',
      isScroll: true,
      custom: false,
      clsBtn: false, /* full modal 하단 플로팅 닫기 버튼*/
      closeCallback: null,
      contentClassName: '',
    }

    if (!_.isEmpty(this.generalPopupQueue)) {
      const existsPopup = _.find(this.generalPopupQueue, { popupId })
      console.log('========= addGeneralPopup', popupId, existsPopup)
      if (existsPopup) {
        return
      }
    }

    console.log('================ lhn options', options)
    const popup = {
      contentBody,
      title,
      options: { ...defaultOptions, ...options },
      titleCustom,
      popupId: popupId ?? `generalPopup:${uuid.v4()}`,
    }

    this.generalPopupQueue.push(popup)
    this.currentGeneralPopup = popup

    this.addVisibleModalList(popup.popupId)
  }

  removeBodyScrollHiddenClass = () => {
    if (!_.isEmpty(this.generalPopupQueue)) {
      return
    }
    if (!_.isEmpty(this.dialogQueue)) {
      return
    }
    if (_.size(this.visibleModalList) > 0) {
      return
    }
    const bodyScrollClassName = BODY_SCROLL_HIDDEN
    const bodyXScrollable = BODY_X_SCROLLABLE
    const disableTouchActionClass = TOUCH_ACTION_DISABLED
    const enableTouchActionClass = TOUCH_ACTION_ENABLED

    const { body } = document
    const { classList } = body
    classList.remove(bodyScrollClassName, bodyXScrollable)

    const appElem = document.getElementById('app')
    if (isIOS && isMobile) {
      appElem.classList.remove(disableTouchActionClass, enableTouchActionClass)
    }

    const foundHtmlClassList = document.getElementsByTagName('html')[0].classList
    _.forEach(foundHtmlClassList, (item) => {
      foundHtmlClassList.remove(item)
    })
  }

  addBodyScrollHiddenClass(): void {
    if (isMobile) {
      // 백스크롤 제어를 위한 body className
      const bodyScrollClassName = BODY_SCROLL_HIDDEN
      // IOS의 백스크롤 제어를 위한 app className
      const disableTouchActionClass = TOUCH_ACTION_DISABLED

      const { body } = document
      const { classList } = body

      const appElem = document.getElementById('app')

      if (!classList.contains(bodyScrollClassName)) {
        classList.add(bodyScrollClassName)
      }
      if (isIOS && !appElem.classList.contains(disableTouchActionClass)) {
        appElem.classList.add(disableTouchActionClass)
      }
    }
  }

  // 백스크롤 제어 및 스크롤 위치를 최상단에 위치시키기 위한 className
  addAppScrollHiddenClass(): void {
    if (isMobile) {
      const appElem = document.getElementById('app')
      if (!appElem.classList.contains('app-scroll-hidden')) {
        appElem.classList.add('app-scroll-hidden')
      }
    }
  }

  removeAppScrollHiddenClass = () => {
    const appElem = document.getElementById('app')
    if (isMobile) {
      appElem.classList.remove('app-scroll-hidden')
    }
  }

  addBodyXScrollableClass(): void {
    // 리그 필터 팝업 호출 시 강제로 body에 overflow: hidden이 적용되기 때문에 추가
    // --> PC버전에서 페이지 형태의 팝업이기때문에 좌우 스크롤이 가능해야함
    if (isMobile) {
      return
    }
    const bodyScrollClassName = BODY_X_SCROLLABLE

    const { body } = document
    const { classList } = body

    if (!classList.contains(bodyScrollClassName)) {
      classList.add(bodyScrollClassName)
    }
  }

  @action
  resetGeneralPopupProperties = () => {
    this.currentGeneralPopup = null
    this.generalPopupQueue.forEach(queue => this.removeVisibleModalList(queue.popupId))
    this.generalPopupQueue = []
    this.removeBodyScrollHiddenClass()
  }

  @action
  resetPushDialog = () => {
    this.currentDialog = null
    this.dialogQueue.forEach(queue => this.removeVisibleModalList(queue.id))
    this.dialogQueue = []
    this.removeBodyScrollHiddenClass()
  }

  @action
  toMoveWebHomePage = (url, target = 'self') => {
    if (android) {
      android.jnRequestOpenBrowser(url)
    } else if (!isMobile || target === 'popup') {
      window.open(url, '_blank')
      window.focus()
    } else if (target === 'self') {
      window.location.href = url
    }
  }

  @action
  updateVisibleModalList(visible: boolean, key: string): void {
    if (visible) {
      this.addVisibleModalList(key)
    } else {
      this.removeVisibleModalList(key)
    }
  }

  @action
  pushDialog = (type: DialogType, params: DialogOptions): DialogProps => {
    if (this.currentDialog && this.currentDialog.id === params.id) {
      return this.currentDialog
    }

    if (!_.isEmpty(this.dialogQueue)) {
      const existsDialog = _.find(this.dialogQueue, { id: params.id })
      if (existsDialog) {
        return existsDialog
      }
    }

    const dlg = params as DialogProps
    dlg.id = params.id || `pushDialog:${uuid.v4()}`
    dlg.type = type
    dlg.callbackClose = this.closeDialog

    this.dialogQueue.push(dlg)
    this.currentDialog = dlg

    this.addVisibleModalList(dlg.id)

    return dlg
  }
  @action
  pushDialogYesNo = (params: DialogOptions): DialogProps => {
    params.negativePress = params.negativePress || '아니오'
    params.positivePress = params.positivePress || '예'
    return this.pushDialog(DialogType.CHOICE, params)
  }

  @action
  pushDialogWait = (params: DialogOptions): DialogProps => {
    return this.pushDialog(null, params)
  }

  @action
  pushDialogOk = (params: DialogOptions): DialogProps => {
    params.singlePress = params.singlePress || '확인'
    return this.pushDialog(DialogType.SINGLE, params)
  }

  @action
  closeGeneralPopupByBackButton = (popupId: string): void => {
    this.backButtonClosePopupId = popupId
  }

  hasGeneralPopupBackHistory = (): boolean => {
    return this.generalPopupQueue.some(popup => popup.options?.enableBackHistory)
  }

  @action
  clearBackButtonClosePopupId = (): void => {
    this.backButtonClosePopupId = null
  }

  @action
  closeDialog = (): void => {
    const index = _.findIndex(this.dialogQueue, { id: this.currentDialog?.id })
    if (index > -1) {
      this.dialogQueue.splice(index, 1)
      this.removeVisibleModalList(this.currentDialog.id)
    } else {
      return
    }
    this.currentDialog = this.dialogQueue[this.dialogQueue.length - 1]

    if (_.isEmpty(this.currentDialog)) {
      this.currentDialog = undefined
    }
  }

  @action
  closeGeneralPopup = (popupId?: string, isAnimated?: boolean): void => {
    const index = _.findIndex(this.generalPopupQueue, {
      popupId: popupId || this.currentGeneralPopup?.popupId,
    })
    console.log('=========== closeGeneralPopup', popupId, index)
    if (index <= -1) {
      return
    }

    const popupToRemove = this.generalPopupQueue[index]
    const timer = isAnimated ? 300 : 0
    setTimeout(() => {
      action(() => {
        this.generalPopupQueue.splice(index, 1)
        this.removeVisibleModalList(popupToRemove.popupId)

        if (_.isEmpty(this.generalPopupQueue)) {
          this.currentGeneralPopup = null
        } else {
          this.currentGeneralPopup = this.generalPopupQueue[this.generalPopupQueue.length - 1]
        }
      })()
    }, timer)
  }

  @action
  setChattingLastMessage = (lastMessage: string) => {
    this.chattingLastMessage = lastMessage
  }

  @action
  getGlobalSettings = async () => {
    try {
      const { data } = await this.api.game.getGlobalSettings()
      runInAction(() => {
        this.globalSettings = data
      })
    } catch (err) {
      message.error(err.message)
    }
  }

  @action
  findGlobalSetting = (key: string): GlobalSettings => {
    return _.find(this.globalSettings, { key })
  }

  // NOTE: 로그인 후 menuAvailableList에 값이 있는 경우 메뉴버튼들을 보여준다
  getMenuAvailableList = () => {
    const data = this.findGlobalSetting(GlobalStoreKey.V2_MENU_AVAILABLE_LIST)
    if (data) {
      this.menuAvailableList = JSON.parse(data.value)
      return this.menuAvailableList
    }

    return this.menuAvailableList
  }

  getBettorFindTabOptions = () => {
    const baseSearchGlobalProperty = this.findGlobalSetting(GlobalStoreKey.BETTOR_FIND_BASE_SEARCH_OPTIONS)
    const baseSearch = safeJsonParse(baseSearchGlobalProperty?.value) || { enabled: false }

    const todayBettingGlobalProperty = this.findGlobalSetting(GlobalStoreKey.BETTOR_FIND_TODAY_BETTING_BETTOR_OPTIONS)
    const todayBetting = safeJsonParse(todayBettingGlobalProperty?.value) || { enabled: false }

    const customTabGlobalProperty = this.findGlobalSetting(GlobalStoreKey.BETTOR_FIND_CUSTOM_TAB_OPTIONS)
    const customTab = safeJsonParse(customTabGlobalProperty?.value) || { enabled: false, tabName: '' }

    const betSuccessGlobalProperty = this.findGlobalSetting(GlobalStoreKey.BETTOR_FIND_BET_SUCCESS_BETTOR_OPTIONS)
    const betSuccess = safeJsonParse(betSuccessGlobalProperty?.value) || { enabled: false }

    return { baseSearch, todayBetting, customTab, betSuccess }
  }

  isDisabledBettorFindMenu = (): boolean => {
    const options = this.getBettorFindTabOptions()

    return !options.baseSearch.enabled
      && !options.todayBetting.enabled
      && !options.customTab.enabled
      && !options.betSuccess.enabled
  }

  getMissionEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.MISSION_ENABLED)
    if (globalValue) {
      const value = globalValue.value
      return value === '1'
    }

    return false
  }

  getIngameImagesUrl = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.INGAME_IMAGES_URL)
    if (globalValue) {
      return globalValue.value
    }

    return null
  }

  // 이미지 캐시 무효화용 버전 쿼리 반환 (예: ?v=202606041235). 미설정 시 빈 문자열
  getIngameImagesVersion = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.INGAME_IMAGES_VERSION)
    if (globalValue && globalValue.value) {
      return `?v=${globalValue.value}`
    }

    return ''
  }

  getFirstBetMoneyReward = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.FIRST_BET_MONEY_REWARD)
    if (globalValue) {
      return Number(globalValue.value)
    }

    return 2000
  }

  getGuideBonus = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.GUIDE_BONUS)
    if (globalValue) {
      return Number(globalValue.value)
    }

    return 30000
  }

  getGuideBonusEnabled = () => {
    const guideBonusGlobalData = this.findGlobalSetting(GlobalStoreKey.GUIDE_BONUS_ENABLED)
    return guideBonusGlobalData?.value === '1' ?? false
  }

  getClubLimBetOnOff = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.CLUB_GMONEY_MAX_BET_LIMIT_ON_OFF)
    if (globalValue) {
      const value = globalValue.value.toLowerCase()
      return value === 'on'
    }

    return false
  }

  getClubIdFromUrl = () => {
    const pathName = routerStore.location.pathname
    if (pathName) {
      const [, pageName, , clubId] = pathName.split('/')
      if (pageName === 'matchup') {
        return clubId
      }
    }
    return null
  }

  getVisualizationUrl = (
    matchId: number,
    sportId: number,
    matchType = '',
    feedSupplier = FEED_SUPPLIER.BET_RADAR,
    matchInfoTrack?: SimpleMatchTrack,
  ) => {
    let visualizationUrl = this.findGlobalSetting('visualization_url')?.value
    let visualizationAuthKey = this.findGlobalSetting('visualization_auth_key')?.value
    // https://entr.adventurer.co.kr:7778/?authKey=ec8d9f81-2bcb-41b1-ac48-48e9ac690d74&matchId=22774269&sportId=1&momentum=disable&tabsPosition=top&layout=double
    if (_.isEmpty(visualizationUrl)) {
      visualizationUrl = 'https://entr.adventurer.co.kr/?authKey={0}&matchId={1}&sportId={2}&momentum=disable&tabsPosition=top&layout={3}&matchType={4}&feedSupplier={5}&lN={6}&hN={7}&aN={8}&hE={9}&aE={10}&mD={11}&mT={12}'
    }
    if (_.isEmpty(visualizationAuthKey)) {
      visualizationAuthKey = 'ec8d9f81-2bcb-41b1-ac48-48e9ac690d74'
    }

    // http://entr.adventurer.co.kr:7778/?authKey={0}&matchId={1}&sportId={2}&momentum=disable&tabsPosition=top&layout={3}
    visualizationUrl = _.chain(visualizationUrl)
      .replace('{0}', visualizationAuthKey)
      .replace('{1}', _.toString(matchId))
      .replace('{2}', _.toString(sportId))
      .replace('{3}', 'double')
      .replace('{4}', matchType)
      .replace('{5}', feedSupplier)
      .value()
    if (matchInfoTrack) {
      const dateOfMatch = matchInfoTrack.dateOfMatch
      const dateAt = dateFormat(dateOfMatch, 'mm/dd')
      const dayAt = ArrDayText[new Date(dateOfMatch).getDay()]
      const timeAt = dateFormat(dateOfMatch, 'HH:MM')
      visualizationUrl = _.chain(visualizationUrl)
        .replace('{6}', matchInfoTrack.tournamentName || '리그') // 리그이름
        .replace('{7}', matchInfoTrack.homeTeamName) // 홈템이름
        .replace('{8}', matchInfoTrack.awayTeamName) // 원정이름
        .replace('{9}', matchInfoTrack.homeTeamEmblem) // 홈팀 엠블럼
        .replace('{10}', matchInfoTrack.awayTeamEmblem) // 원정 엠블럼
        .replace('{11}', `${dateAt}(${dayAt})`) // 매치 날짜
        .replace('{12}', timeAt) // 매치 시간
        .value()
    }
    return visualizationUrl
  }

  // getVisualizationUrl = (
  //   matchId: number,
  //   sportId: number,
  //   matchType = '',
  //   feedSupplier = FEED_SUPPLIER.BET_RADAR,
  //   detailTrack?: DetailMatchTrack,
  // ) => {
  //   let visualizationUrl = this.findGlobalSetting('visualization_url')?.value
  //   let visualizationAuthKey = this.findGlobalSetting('visualization_auth_key')?.value
  //   // https://entr.adventurer.co.kr:7778/?authKey=ec8d9f81-2bcb-41b1-ac48-48e9ac690d74&matchId=22774269&sportId=1&momentum=disable&tabsPosition=top&layout=double
  //   if (_.isEmpty(visualizationUrl)) {
  //     visualizationUrl = 'https://entr.adventurer.co.kr/?authKey={0}&matchId={1}&sportId={2}&momentum=disable&tabsPosition=top&layout={3}&matchType={4}&feedSupplier={5}&lN={6}&hN={7}&aN={8}&hE={9}&aE={10}&mD={11}&mT={12}'
  //   }
  //   if (_.isEmpty(visualizationAuthKey)) {
  //     visualizationAuthKey = 'ec8d9f81-2bcb-41b1-ac48-48e9ac690d74'
  //   }
  //
  //   // http://entr.adventurer.co.kr:7778/?authKey={0}&matchId={1}&sportId={2}&momentum=disable&tabsPosition=top&layout={3}
  //   visualizationUrl = _.chain(visualizationUrl)
  //     .replace('{0}', visualizationAuthKey)
  //     .replace('{1}', _.toString(matchId))
  //     .replace('{2}', _.toString(sportId))
  //     .replace('{3}', 'double')
  //     .replace('{4}', matchType)
  //     .replace('{5}', feedSupplier)
  //     .value()
  //   if (detailTrack && detailTrack.track) {
  //     const track = detailTrack.track
  //     const dateOfMatch = track.dateOfMatch
  //     const dateAt = dateFormat(dateOfMatch, 'mm/dd')
  //     const dayAt = ArrDayText[new Date(dateOfMatch).getDay()]
  //     const timeAt = dateFormat(dateOfMatch, 'HH:MM')
  //     visualizationUrl = _.chain(visualizationUrl)
  //       .replace('{6}', detailTrack.tournamentName || '리그') // 리그이름
  //       .replace('{7}', track.homeTeamName) // 홈템이름
  //       .replace('{8}', track.awayTeamName) // 원정이름
  //       .replace('{9}', track.homeTeamEmblem) // 홈팀 엠블럼
  //       .replace('{10}', track.awayTeamEmblem) // 원정 엠블럼
  //       .replace('{11}', `${dateAt}(${dayAt})`) // 매치 날짜
  //       .replace('{12}', timeAt) // 매치 시간
  //       .value()
  //   }
  //   return visualizationUrl
  // }

  getStatVisualizationUrl =
    (matchId: number, sportId: number, seasonId: number, homeTeamId: number, awayTeamId: number) => {
      const sportIds = JSON.parse(
        this.findGlobalSetting(GlobalStoreKey.STAT_VISUALIZATION_MATCH_FOCUS_SPORT_IDS)?.value ?? '[]',
      )

      let url
      if (_.includes(sportIds, sportId)) {
        url = this.findGlobalSetting(GlobalStoreKey.STAT_VISUALIZATION_MATCH_FOCUS_URL)?.value
      } else {
        url = this.findGlobalSetting(GlobalStoreKey.STAT_VISUALIZATION_MATCH_NORMAL_URL)?.value
      }

      if (_.isEmpty(url)) {
        url = 'https://entr.adventurer.co.kr/adventurer/ko/{sportId}/season/{seasonId}/h2h/{homeTeamId}/{awayTeamId}/match/{matchId}'
      }

      url = _.chain(url)
        .replace('{sportId}', _.toString(sportId))
        .replace('{seasonId}', _.toString(seasonId))
        .replace('{homeTeamId}', _.toString(homeTeamId))
        .replace('{awayTeamId}', _.toString(awayTeamId))
        .replace('{matchId}', _.toString(matchId))
        .value()
      return url
    }

  getExtraInningInfoVisibleSportIds = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.EXTRA_INNING_INFO_VISIBLE_SPORT_IDS)
    if (globalValue) {
      return globalValue.value.split(',')
    }

    return []
  }

  getNoExtraInningInfoVisibleSportIds = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.NO_EXTRA_INNING_INFO_VISIBLE_SPORT_IDS)
    if (globalValue) {
      return globalValue.value.split(',')
    }

    return []
  }

  getTutorialEnabled = () => {
    const globalData = this.findGlobalSetting(GlobalStoreKey.TUTORIAL_ENABLED)
    return globalData?.value === '1' ?? false
  }

  getScoreModeEnabled = () => {
    const globalData = this.findGlobalSetting(GlobalStoreKey.SCORE_MODE_ENABLED)
    return globalData?.value === '1' ?? false
  }

  getScorePageTitle = () => {
    return this.getScoreModeEnabled() ? 'SCORE' : '경기결과'
  }

  getUseDia = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.USE_DIA)
    if (globalValue) {
      return globalValue?.value === '1'
    }
    return false
  }

  getPaymentRewardEventEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.PAYMENT_REWARD_EVENT_ENABLED)
    if (globalValue) {
      return globalValue?.value === '1'
    }
    return false
  }

  getGmoneyChargeInducePopupEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.GMONEY_CHARGE_INDUCE_POPUP_ENABLED)
    return globalValue?.value === '1' ?? false
  }

  getDiaAdvertisementImageDisplayEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.DIA_ADVERTISEMENT_IMAGE_DISPLAY_ENABLED)
    return globalValue?.value === '1' ?? false
  }

  getDiaAdvertisementImageDisplayCycleDay = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.DIA_ADVERTISEMENT_IMAGE_DISPLAY_CYCLE_DAY)
    return globalValue?.value ?? 1
  }

  getAppleLoginEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.APPLE_LOGIN_ENABLED)
    return globalValue?.value === '1' ?? false
  }

  getCasinoAdvertisementPopupDisplayCycleDay = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.CASINO_ADVERTISEMENT_POPUP_DISPLAY_CYCLE_DAY)
    return globalValue?.value ?? 1
  }

  getCasinoAdvertisementButtonDisplayEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.CASINO_ADVERTISEMENT_BUTTON_DISPLAY_ENABLED)
    return globalValue?.value === '1' ?? false
  }

  getCasinoAdvertisementPopupDisplayEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.CASINO_ADVERTISEMENT_POPUP_DISPLAY_ENABLED)
    return globalValue?.value === '1' ?? false
  }

  getBettingSlipKeepCartExplainImageDisplayEnabled = () => {
    const globalValue = this.findGlobalSetting(
      GlobalStoreKey.BETTING_SLIP_KEEP_CART_EXPLAIN_IMAGE_DISPLAY_ENABLED,
    )
    return globalValue?.value === '1' ?? false
  }

  getUserIdsForTest = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.USER_ID_LIST_FOR_TESTING)

    if (globalValue) {
      const value = _.trim(globalValue.value)
      return JSON.parse(value) || []
    }

    return []
  }

  getGodBlessItemEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.GOD_BLESS_ITEM_ENABLED)
    return globalValue?.value === '1' ?? false
  }

  getGodBlessItemAvailableFoldCondition = (): ExtraOddsItemAvailableFoldCondition => {
    const value = this.findGlobalSetting(GlobalStoreKey.GOD_BLESS_ITEM_AVAILABLE_FOLD_CONDITION)?.value
    if (_.isEmpty(value)) {
      return {
        minimumOutcomeRate: 1.3,
        minimumOutcomeRateFoldCount: 2,
      }
    }
    return JSON.parse(value)
  }

  getBetLimitScoreInfoBySportId = (sportId: number): BetLimitScoreInfo => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.LIVE_BETABLE_MATCH_SCORE)?.value
    if (globalValue) {
      const parse = JSON.parse(globalValue) || []
      return _.find(parse, { sportId })
    }

    return null
  }

  getBetLimitScoreInfoMap = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.LIVE_BETABLE_MATCH_SCORE)?.value
    if (globalValue) {
      const parse = JSON.parse(globalValue) || [] as BetLimitScoreInfo[]
      return _.keyBy(parse, 'sportId')
    }

    return []
  }

  getMatchupEtcFilterAlertOptions = (): MatchupEtcFilterAlertOptions => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.MATCHUP_ETC_FILTER_ALERT_OPTIONS)?.value
    if (globalValue) {
      return JSON.parse(globalValue) || {}
    }
    return { enabled: false }
  }

  isHomeBannerRolling = (): boolean => {
    let isRolling = false
    const globalValue = this.findGlobalSetting(GlobalStoreKey.HOME_BANNER_ROLLING_SETTINGS).value

    if (globalValue) {
      const homeBannerRolling = JSON.parse(globalValue)
      isRolling = homeBannerRolling?.enabled || false
    }

    return isRolling
  }

  isHomeMainBannerRolling = (): boolean => {
    let isRolling = false
    const globalValue = this.findGlobalSetting(GlobalStoreKey.HOME_MAIN_BANNER_ROLLING_SETTINGS).value

    if (globalValue) {
      const homeMainBannerRolling = JSON.parse(globalValue)
      isRolling = homeMainBannerRolling?.homeMainBannerRollingEnabled || false
    }
    return isRolling
  }

  getHomeBannerRollingIntervalTime = (): number => {
    let intervalTime = 0
    const globalValue = this.findGlobalSetting(GlobalStoreKey.HOME_BANNER_ROLLING_SETTINGS).value

    if (globalValue) {
      const homeBannerRolling = JSON.parse(globalValue)
      intervalTime = homeBannerRolling?.time || 0
    }

    return intervalTime
  }

  getHomeMainBannerRollingIntervalTime = (): number => {
    let intervalTime = 0
    const globalValue = this.findGlobalSetting(GlobalStoreKey.HOME_MAIN_BANNER_ROLLING_SETTINGS).value

    if (globalValue) {
      const homeBannerRolling = JSON.parse(globalValue)
      intervalTime = homeBannerRolling?.homeMainBannerRollingSecond || 0
    }

    return intervalTime
  }

  getFriendInvitationEnabled = (): boolean => {
    return _.toString(this.findGlobalSetting(GlobalStoreKey.FRIEND_INVITATION_ENABLED)?.value) === '1' ?? false
  }

  getUserBlockEnabled = (): boolean => {
    return _.toString(this.findGlobalSetting(GlobalStoreKey.COMMUNICATION_BLOCK_ENABLED)?.value) === '1' ?? false
  }

  isPossibleUserBlockByUserPromiseId = (userPromiseId: string): boolean => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.COMMUNICATION_BLOCK_EXCLUDE_PROMISE_IDS)?.value

    if (globalValue) {
      const excludeBlockUserPromiseIds = JSON.parse(globalValue)
      return !_.includes(excludeBlockUserPromiseIds, userPromiseId)
    }

    return true
  }

  getAccountTransferOptions = (): AccountTransferOptions => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.ACCOUNT_TRANSFER_OPTIONS)?.value

    if (globalValue) {
      return JSON.parse(globalValue)
    }

    return null
  }

  isAccountTransferEnabled = (): boolean => {
    let enable = false
    const globalValue = this.findGlobalSetting(GlobalStoreKey.ACCOUNT_TRANSFER_OPTIONS)?.value

    if (globalValue) {
      const accountTransferOtps = JSON.parse(globalValue)
      enable = accountTransferOtps?.enable >= 1 || false
    }

    return enable
  }

  isAccountTransferExpired = (): boolean => {
    const options = this.getAccountTransferOptions()

    if (!_.isEmpty(options?.period?.to)) {
      return dayjs().diff(dayjs(options.period.to)) >= 0
    }

    return false
  }

  isAccountTransferInfoHideEnabled = () => {
    const options = this.getAccountTransferOptions() as AccountTransferOptions
    return options?.popupHideEnable === 1 || false
  }

  isAccountTransferVisible = (): boolean => {
    const isPopupHideEnabled = this.isAccountTransferInfoHideEnabled()
    const lastPopupShowTime = StorageUtils.getShowAccountTransferInfo()

    if (!isPopupHideEnabled || lastPopupShowTime === 0) {
      return true
    }

    const lastShotDate = dayjs(lastPopupShowTime).format('YYYY-MM-DD')
    const deltaTime = dayjs(lastShotDate).add(1, 'day').diff(dayjs())

    return deltaTime < 0
  }

  getChatContentButtonOptions(): ChatContentButtonOption[] {
    const data = this.findGlobalSetting(GlobalStoreKey.CHAT_CONTENT_BUTTON_OPTIONS)
    if (data) {
      return JSON.parse(data.value)
    }
    return null
  }

  getMajorUserGroupThemeList(): MajorUserGroupThemeInfo[] {
    const data = this.findGlobalSetting(GlobalStoreKey.MAJOR_USER_GROUP_MY_PAGE_THEME_SETTINGS)
    if (data) {
      return JSON.parse(data.value)
    }
    return null
  }

  getSportTalkMatchTimelineEnabled(): boolean {
    const data = this.findGlobalSetting(GlobalStoreKey.SPORT_TALK_MATCH_TIMELINE_OPTIONS)
    if (data) {
      const options = JSON.parse(data.value)
      return options.enabled
    }
    return false
  }

  getMatchListBannerOrder(): number {
    const data = this.findGlobalSetting(GlobalStoreKey.MATCH_LIST_BANNER_ORDER)
    let order = 0
    if (data) {
      order = _.toNumber(data.value)
    }
    return order
  }

  getAndroidChampPokerDeepLink(): ChampPokerDeepLink {
    const data = this.findGlobalSetting(GlobalStoreKey.CHAMPPOKER_DEEPLINK_OPTIONS)
    if (data) {
      const deepLink = JSON.parse(data.value)
      return deepLink?.android
    }
    return null
  }

  getIosChampPokerDeepLink(): ChampPokerDeepLink {
    const data = this.findGlobalSetting(GlobalStoreKey.CHAMPPOKER_DEEPLINK_OPTIONS)
    if (data) {
      const deepLink = JSON.parse(data.value)
      return deepLink?.ios
    }
    return null
  }

  getUiThemeSetting(): string {
    const theme = this.findGlobalSetting(GlobalStoreKey.INGAME_UI_THEME)?.value
    if (theme && theme.length > 0) {
      return theme
    }
    return 'default'
  }

  @action
  setServerState = (state: SERVER_STATE) => {
    this.serverState = state
  }

  @action
  setVisibleMissionListPopup = (visible: boolean) => {
    this.visibleMissionListPopup = visible
    this.updateVisibleModalList(visible, 'visibleMissionListPopup')
  }

  @action
  setVisibleCouponInputPopup = (visible: boolean) => {
    this.visibleCouponInputPopup = visible
    this.updateVisibleModalList(visible, 'visibleCouponInputPopup')
  }

  @action
  setVisibleTutorialPopup = (visible: boolean) => {
    this.visibleTutorialPopup = visible
    this.updateVisibleModalList(visible, 'visibleTutorialPopup')
  }

  @action
  setVisibleTutorialVideoPopup = (visible: boolean) => {
    this.visibleTutorialVideoPopup = visible
    this.updateVisibleModalList(visible, 'visibleTutorialVideoPopup')
  }

  @action
  setUsageGuideVideoPopupPlaybackLocked = (locked: boolean) => {
    this.usageGuideVideoPopupPlaybackLocked = locked
  }

  getUsageGuideVideoPopupOptions = (): UsageGuideVideoPopupOptions => {
    const defaultOptions: UsageGuideVideoPopupOptions = {
      enabled: true,
      videoFileName: '',
      autoDisplayEnabled: true,
      autoDisplayFirstJoinAtFrom: '2026-08-01',
    }
    const globalValue = this.findGlobalSetting(GlobalStoreKey.USAGE_GUIDE_VIDEO_POPUP_OPTIONS)
    if (!globalValue?.value) {
      return defaultOptions
    }
    try {
      return { ...defaultOptions, ...JSON.parse(globalValue.value) }
    } catch (err) {
      console.error(err)
      return defaultOptions
    }
  }

  // videoFileName이 비어 있으면 재생할 URL을 만들 수 없으므로 빈 문자열을 반환한다.
  getUsageGuideVideoUrl = (videoFileName: string): string => {
    const ingameVideoUrl = this.findGlobalSetting(GlobalStoreKey.INGAME_VIDEO_URL)?.value
    if (!videoFileName || !ingameVideoUrl) {
      return ''
    }
    return `${ingameVideoUrl}${videoFileName}`
  }

  @action
  setVisibleBaccaratGuidePopup = (visible: boolean) => {
    this.visibleBaccaratGuidePopup = visible
    this.updateVisibleModalList(visible, 'visibleBaccaratGuidePopup')
  }

  @action
  setVisibleCasinoOpenPopup = (visible: boolean) => {
    this.visibleCasinoOpenPopup = visible
    this.updateVisibleModalList(visible, 'visibleCasinoOpenPopup')
  }

  @action
  setVisibleGuideBonusReceiveButton = (visible: boolean) => {
    this.visibleGuideBonusReceiveButton = visible
    // this.updateVisibleModalList(visible, 'visibleGuideBonusReceiveButton') // 해당하는 모달 존재하지 않음
  }

  @action
  setVisibleChangeProfile = (visible: boolean): void => {
    this.visibleChangeProfile = visible
    this.updateVisibleModalList(visible, 'visibleChangeProfile')
  }

  @action
  setVisibleTodayPick = (visible: boolean) => {
    this.visibleTodayPick = visible
  }

  @action
  setVisibleTodayPickList = (visible: boolean) => {
    this.visibleTodayPickList = visible
  }

  @action
  setVisibleUserPick = (visible: boolean) => {
    this.visibleUserPick = visible
  }

  @action
  setVisibleAdvNews = (visible: boolean) => {
    this.visibleAdvNews = visible
  }

  @action
  setVisibleGuide = (visible: boolean) => {
    this.visibleGuide = visible
  }

  @action
  setVisibleGuideBonusDelay = (visible: boolean) => {
    this.visibleGuideBonusDelay = visible
  }

  @action
  setVisibleSelectLiveMatchType = (visible: boolean) => {
    this.visibleSelectLiveMatchType = visible
    this.updateVisibleModalList(visible, 'visibleSelectLiveMatchType')
  }

  @action
  setVisibleSelectCertification = (visible: boolean) => {
    this.visibleSelectCertification = visible
    this.updateVisibleModalList(visible, 'visibleSelectCertification')
  }

  isAvailableMenu = (sideMenu: SIDE_MENU) => {
    return this.menuAvailableList.find(menu => menu === sideMenu)
  }

  setBettingHistoryTabInitialTabKey = (key: BETTING_HISTORY_TAB_KEY) => {
    this.bettingHistoryTabInitialTabKey = key
  }

  setMatchupDetailTabInitialIndex = (index: MATCHUP_DETAIL_STATIC_FILTER_INDEX) => {
    this.matchupDetailTabInitialIndex = index
  }

  @action
  setVisibleLiveBetCounter = (visible: boolean) => {
    clearTimeout(this.liveBetCounterVisibleTimer)
    this.visibleLiveBetCounter = visible
  }

  @action
  setVisibleSendMessagePopup(visible: boolean) {
    this.visibleSendMessagePopup = visible
    this.updateVisibleModalList(visible, 'visibleSendMessagePopup')
  }

  setAuthenticationPopup(popup) {
    this.authenticationPopup = popup
  }

  @action
  setBubblePopupType = (type: BubbleTextType, bubbleType: BubbleType) => {
    this.bubbleData[bubbleType].type = type
  }

  @action
  setBubblePopupVisible = (visible: boolean, bubbleType: BubbleType) => {
    this.bubbleData[bubbleType].visible = visible
  }

  @action
  setBubbleText = (text: string, bubbleType: BubbleType) => {
    this.bubbleData[bubbleType].text = text
  }

  @action
  setVisibleCompleteAni = (visible: boolean) => {
    this.visibleBetCompleteAni = visible
  }

  @action
  setVisibleComponentCompleteAni = (visible: boolean) => {
    this.visibleComponentBetCompleteAni = visible
  }

  @action
  setVisibleMessageAlertAni = (visible: boolean) => {
    this.visibleMessageAlertAni = visible
  }

  @action
  setVisibleUrgencyMessagePopup = (visible: boolean) => {
    this.visibleUrgencyMessagePopup = visible
    this.updateVisibleModalList(visible, 'visibleUrgencyMessagePopup')
  }

  @action
  setVisibleMatchFilterPopup = (visible: boolean) => {
    this.visibleMatchFilterPopup = visible
    this.updateVisibleModalList(visible, 'visibleMatchFilterPopup')

    if (visible) {
      // 리그 필터 팝업 호출 시 강제로 body에 overflow: hidden이 적용되기 때문에 추가
      // --> PC버전에서 페이지 형태의 팝업이기때문에 좌우 스크롤이 가능해야함
      this.addBodyXScrollableClass()
    }
  }

  @action
  setVisibleMatchupFilterSelectPopup = (visible: boolean): void => {
    this.visibleMatchupFilterSelectPopup = visible
    this.updateVisibleModalList(visible, 'visibleMatchupFilterSelectPopup')
  }

  @action
  showBubblePopupTimer(type: BubbleTextType, text: string, bubbleType: BubbleType) {
    clearTimeout(this.bubbleData[bubbleType].timerId)
    this.setBubblePopupType(type, bubbleType)
    this.setBubblePopupVisible(true, bubbleType)
    this.setBubbleText(text, bubbleType)
    this.bubbleData[bubbleType].timerId = setTimeout(() => {
      this.initBubble()
    }, BUBBLE_SECOND_DURATION * 1000)
  }

  showBetCompleteAniTimer = () => {
    clearTimeout(this.visibleBetCompleteAniTimer)
    this.setVisibleCompleteAni(true)
    this.visibleBetCompleteAniTimer = setTimeout(() => {
      this.setVisibleCompleteAni(false)
    }, BUBBLE_SECOND_DURATION * 1000)
  }

  showComponentBetCompleteAniTimer = () => {
    clearTimeout(this.visibleBetCompleteAniTimer)
    this.setVisibleComponentCompleteAni(true)
    this.visibleBetCompleteAniTimer = setTimeout(() => {
      this.setVisibleComponentCompleteAni(false)
    }, COMPONENT_SECOND_DURATION * 1000)
  }

  showMessageAlertAniTimer = () => {
    clearTimeout(this.visibleBetCompleteAniTimer)
    this.setVisibleMessageAlertAni(true)
    this.visibleBetCompleteAniTimer = setTimeout(() => {
      this.setVisibleMessageAlertAni(false)
    }, COMPONENT_SECOND_DURATION * 1000)
  }

  closeAuthenticationPopup() {
    if (this.authenticationPopup) {
      this.authenticationPopup.close()
      this.authenticationPopup = null
    }
  }

  @action
  setVisibleNoAccess(visible: boolean) {
    this.visibleNoAccess = visible
    this.updateVisibleModalList(visible, 'visibleNoAccess')
  }

  getClubGmoneyMaxBetLimit = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.CLUB_GMONEY_MAX_BET_LIMIT)
    if (globalValue) {
      return _.toNumber(globalValue.value)
    }

    return 700000
  }

  async uploadCommentImageToAwsS3(imageFile: File): Promise<string> {
    const response = await this.api.v2.uploadCompanyPickCommentImageFile(imageFile)
    return response.data
  }

  async uploadChatImageToAwsS3(imageFile: File): Promise<string> {
    const response = await this.api.v2.uploadChatImageFile(imageFile)
    return response.data
  }

  @action
  setVisibleMatchupDetailPopup(visible: boolean): void {
    this.visibleMatchupDetailPopup = visible
  }

  @action
  setVisibleTodayUserPickBanner(visible: boolean): void {
    this.visibleTodayUserPickBanner = visible
  }

  @action
  addVisibleModalList(key: string): void {
    this.setVisibleModalList(true, key)
  }

  @action
  removeVisibleModalList(key: string): void {
    this.setVisibleModalList(false, key)
  }

  @action
  setVisibleModalList(plus: boolean, key: string): void {
    if (plus) {
      if (!_.find(this.visibleModalList, { key })) {
        this.visibleModalList = this.visibleModalList.concat([{ key }])
        this.clearActivityScoreNoticeQueue()
      }
    } else if (!_.isEmpty(this.visibleModalList)) {
      const index = _.findIndex(this.visibleModalList, { key })
      if (index > -1) {
        this.visibleModalList.splice(index, 1)
        this.visibleModalList = [...this.visibleModalList]
        this.clearActivityScoreNoticeQueue()
      }
    }
    if (_.size(this.visibleModalList) < 0) {
      this.visibleModalList = []
    }
  }

  @action
  cleanVisibleModalList(): void {
    this.dialogQueue = []
    this.generalPopupQueue = []
    this.currentGeneralPopup = null
    this.currentDialog = null
    this.visibleModalList = []
    this.cleanup()
    this.removeBodyScrollHiddenClass()
  }

  /**
   * 어드민에 등록된 오늘의 승부사 메인 매치업 배너 스케쥴을 체크 하여 강제로 띄워 준다.
   *
   * 10초 마다 체크
   */
  @action
  checkBestPickShowSchedule(): void {
    try {
      const scheduleEnabled = this.findGlobalSetting(GlobalStoreKey.USER_PICK_BEST_PICK_BANNER_ENABLED)?.value
      if (scheduleEnabled === '0') {
        if (this.visibleTodayUserPickBanner) {
          this.setVisibleTodayUserPickBanner(false)
        }
        return
      }
      const hideTime = StorageUtils.getUserPickBestPickBannerHide7Days()
      if (hideTime > new Date().getTime()) {
        if (this.visibleTodayUserPickBanner) {
          this.setVisibleTodayUserPickBanner(false)
        }
        return
      }
      const isBannerHide = StorageUtils.isUserPickBestPickBannerHide()
      let readyShow = false
      if (!isBannerHide) {
        readyShow = true
      } else if (isBannerHide) {
        readyShow = false
        const hideTime = StorageUtils.getUserPickBestPickBannerHideTime()
        const hideDateTime = dayjs(hideTime)
        const showScheduleValue = this.findGlobalSetting(
          GlobalStoreKey.USER_PICK_BEST_PICK_BANNER_SHOW_SCHEDULES,
        )?.value
        if (showScheduleValue) {
          const showSchedules = JSON.parse(showScheduleValue)
          // 스케쥴 설정 예시: 매일 10시, 17시
          // 1) 9시에 닫았다면.. 10시까지 안열리고 10시 이후에 열림
          //   # 00시 이전: 10시 next 는 아직 도달전
          //   # 00시 이후: 10시 next 는 아직 도달전
          //   # 10시전 next 는 현재 시간 대비 아직 도달전이므로 배너 안열림.
          //   # 10시후 next 는 현재 시간 대비 다음날이고 지났으므로 배너 열림.
          // 2) 11시에 닫았다면.. 17시까지 안열리고 17시 이후에 열림
          //   # 위와 동일
          // 3) 18시에 닫았다면.. 다음날 10시까지 안열리고 10시 이후에 열림
          //   # 현재 시간 기준 10시 next 와 17시 next 는 모두 다음날 이므로 배너 안열림.
          // 계속 반복....
          //
          // cron 표현식 next 함수 결과 :
          // * 스케쥴 시간이 지났다면 다음날로 표시
          // * 스케쥴 시간전 이라면 오늘 해당 시간 표시
          for (const showSchedule of showSchedules) {
            const scheduleDate = cronParser.parseExpression(showSchedule).next().toDate()

            // 스케쥴이 실행 된 이후 시간이면 scheduleDate 값은 다음날 이다.
            // 그러므로 스케쥴이 실행 되었는지 판단을 scheduleDate 에서 하루를 빼어 현재 시간과 비교 하여 판단 한다.
            if (dayjs().startOf('days').isSame(dayjs(scheduleDate).add(-1, 'days').startOf('days'))) {
              // 스케쥴 실행 시간 후
              // 마지막으로 배너를 닫은 시간이 스케쥴 시간 보다 이전이면 배너 오픈
              if (dayjs(hideDateTime).isBefore(dayjs(scheduleDate).add(-1, 'days'))) {
                readyShow = true
                break
              }
            }
          }
        }
      }
      if (readyShow && !this.visibleTodayUserPickBanner) {
        StorageUtils.removeUserPickBestUserPickBannerHide()
        this.setVisibleTodayUserPickBanner(true)
      } else if (!readyShow && this.visibleTodayUserPickBanner) {
        this.setVisibleTodayUserPickBanner(false)
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setVisibleReplyPickComment(visible: boolean): void {
    this.visibleReplyPickComment = visible
  }

  @action
  setVisibleEditPickComment(visible: boolean): void {
    this.visibleEditPickComment = visible
  }

  @action
  setVisibleUserTitle(visible: boolean): void {
    this.visibleUserTitle = visible
  }

  @action
  addActivityScoreNoticeQueue(userActivityInfo: UserActivityInfo): void {
    if (userActivityInfo?.showNotification) {
      setTimeout(() => {
        runInAction(() => {
          this.activityScoreNoticeQueue.push(userActivityInfo)
          this.userActivityInfo = { ...userActivityInfo }
        })
      }, 100)
    }
  }

  @action
  clearActivityScoreNoticeQueue(): void {
    this.activityScoreNoticeQueue = []
  }

  @action
  removeActivityScoreNoticeQueue(activityId: number): void {
    const result = _.remove(this.activityScoreNoticeQueue, { activityId })
    if (!_.isEmpty(result)) {
      this.activityScoreNoticeQueue = [].concat(this.activityScoreNoticeQueue)
    }
  }

  @action
  setUserActivityInfo(userActivityInfo: UserActivityInfo): void {
    this.userActivityInfo = userActivityInfo
  }

  @action
  setVisibleAccountTransferPopup(visible: boolean): void {
    this.visibleAccountTransferPopup = visible
  }

  @action
  setAccountTransferPopupCallback(handler: () => Promise<void>) {
    this.accountTransferPopupCallback = handler
  }

  @action
  setVisibleMatchupBottomBannerPopup(visible: boolean): void {
    if (visible) {
      if (componentStore.isShowAvailableMatchupBottomBanner() && this.serverState === SERVER_STATE.ON) {
        componentStore.fetchMatchupBottomBanner().then((data): void => {
          if (data) {
            setTimeout((): void => {
              runInAction((): void => {
                this.visibleMatchupBottomBannerPopup = true
              })
            }, 500)
          }
        })
      }
    } else {
      runInAction((): void => {
        this.visibleMatchupBottomBannerPopup = false
      })
    }
  }

  @action
  setVisiblePopularSearchTerm(visible: boolean) {
    this.visiblePopularSearchTerm = visible
  }

  getPopularSearchTermOptions = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.POPULAR_SEARCH_TERM_OPTIONS)
    if (globalValue) {
      return JSON.parse(globalValue.value)
    }

    return null
  }

  getMinimumLiveMatchOutcomeRate = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.MINIMUM_LIVE_MATCH_OUTCOME_RATE)
    if (globalValue) {
      return _.toNumber(globalValue.value)
    }
    return ENABLE_MIN_BET_RATE
  }

  getSportTalkDashboardEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.SPORT_TALK_DASHBOARD_OPTIONS)
    if (globalValue) {
      return JSON.parse(globalValue.value).pickUserProfileEnabled
    }
    return false
  }

  getChatFloodOptions = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.CHAT_FLOOD_CONTROL_OPTIONS)
    if (globalValue) {
      return JSON.parse(globalValue.value)
    }
    return null
  }

  getRankingButtonEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.RANKING_EVENT_OPTIONS)
    if (globalValue) {
      return JSON.parse(globalValue.value).showRankingButton ?? false
    }
    return false
  }

  getRankingLeagueFilterEnabled = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.RANKING_EVENT_OPTIONS)
    if (globalValue) {
      return JSON.parse(globalValue.value).showRankingLeagueFilter ?? false
    }
    return false
  }

  getRankingImages = () => {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.RANKING_EVENT_OPTIONS)
    if (globalValue) {
      const options = JSON.parse(globalValue.value)
      const baseUrl = this.getIngameImagesUrl()
      const rankingPath = baseUrl ? `${baseUrl}ranking/` : ''

      return {
        mainImage: options.mainImage ? `${rankingPath}${options.mainImage}` : '',
        rewardImage: options.rewardImage ? `${rankingPath}${options.rewardImage}` : '',
        rankingInfoImage: options.rankingInfoImage ? `${rankingPath}${options.rankingInfoImage}` : '',
        precautionsImage: options.precautionsImage ? `${rankingPath}${options.precautionsImage}` : '',
        matchListTagImage: options.matchListTagImage ? `${rankingPath}${options.matchListTagImage}` : '',
      }
    }
    return null
  }

  isChatFloodExcludeUser(userNum: string) {
    const globalValue = this.findGlobalSetting(GlobalStoreKey.CHAT_FLOOD_EXCLUDE_USER_NUMBER_LIST)
    if (globalValue) {
      const list = JSON.parse(globalValue.value)
      return _.includes(list, userNum)
    }
    return false
  }
}

export default new GlobalStore()
